import { useCallback, useMemo, useRef, useState } from 'react';

import { DETECTION_STATUS, IMAGE_CAPTURE } from '@/components/emotion-detector/constants';
import { useApi } from '@/hooks/api';
import { JournalService } from '@/services/emotion-detector';
import {
	EmotionUtils,
	getConfirmedEmotionStatus,
	getDetectedEmotionStatus,
	getErrorStatus,
	TopEmotion,
} from '@/utils/emotion-detector';

export const NUM_SAMPLES = 1;
export const CAPTURE_INTERVAL_MS = 250;

export function sleep(ms: number) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

export function useEmotionDetection() {
	const videoRef = useRef<HTMLVideoElement>(null);
	const apiRequest = useApi();
	const journalService = useMemo(() => new JournalService(apiRequest), [apiRequest]);

	const [preview, setPreview] = useState('');
	const [, setCapturedImages] = useState<string[]>([]);
	const [status, setStatus] = useState<string>(DETECTION_STATUS.INITIAL);
	const [detecting, setDetecting] = useState(false);
	const [pendingEmotion, setPendingEmotion] = useState<TopEmotion | null>(null);
	const [needsConfirm, setNeedsConfirm] = useState(false);
	const [emotion, setEmotion] = useState<TopEmotion | null>(null);
	const [selectedEmotion, setSelectedEmotion] = useState<TopEmotion | null>(null);
	const [journalId, setJournalId] = useState<string | null>(null);

	const startWebcam = useCallback(async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ video: true });
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}
		} catch (error) {
			setStatus(DETECTION_STATUS.WEBCAM_ERROR);
		}
	}, []);

	const captureImages = async (): Promise<string[]> => {
		if (!videoRef.current) return [];

		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		if (!ctx) return [];

		canvas.width = videoRef.current.videoWidth;
		canvas.height = videoRef.current.videoHeight;

		const images: string[] = [];
		for (let i = 0; i < NUM_SAMPLES; i++) {
			ctx.drawImage(videoRef.current, 0, 0);
			const dataUrl = canvas.toDataURL(IMAGE_CAPTURE.FORMAT);
			images.push(dataUrl);
			setPreview(dataUrl);

			if (i < NUM_SAMPLES - 1) {
				await sleep(CAPTURE_INTERVAL_MS);
			}
		}

		setCapturedImages(images);
		return images;
	};

	const detectEmotion = async () => {
		setDetecting(true);

		// Reset all state for fresh detection - like starting from scratch
		setPendingEmotion(null);
		setNeedsConfirm(false);
		setEmotion(null);
		setSelectedEmotion(null);
		setJournalId(null);
		setPreview('');
		setCapturedImages([]);

		setStatus(DETECTION_STATUS.CAPTURING);

		try {
			const images = await captureImages();
			if (images.length === 0) {
				throw new Error('Failed to capture images');
			}

			setStatus(DETECTION_STATUS.ANALYZING);
			const result = await journalService.createJournalEntry(images);

			const normalizedEmotion = EmotionUtils.normalizeEmotion(result.emotion);
			setJournalId(result.id);
			setPendingEmotion(normalizedEmotion);
			setNeedsConfirm(true);
			setStatus(getDetectedEmotionStatus(normalizedEmotion));
		} catch (error: unknown) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			setStatus(getErrorStatus(errorMessage));
		} finally {
			setDetecting(false);
		}
	};

	const confirmDetected = async (): Promise<TopEmotion | null> => {
		if (!pendingEmotion || !journalId) return null;

		setSelectedEmotion(pendingEmotion);
		setEmotion(pendingEmotion);
		setNeedsConfirm(false);

		try {
			// WAIT for annotation API to complete before proceeding
			await journalService.updateAnnotation(journalId, pendingEmotion);

			setStatus(getConfirmedEmotionStatus(pendingEmotion, 'Confirmed'));

			// Return the confirmed emotion to indicate success
			return pendingEmotion;
		} catch (error) {
			// Annotation update failed, ignore silently but don't proceed
			return null;
		}
	};

	const chooseEmotion = async (selectedEmotion: TopEmotion): Promise<TopEmotion | null> => {
		setSelectedEmotion(selectedEmotion);
		setEmotion(selectedEmotion);
		setNeedsConfirm(false);

		if (journalId) {
			try {
				// WAIT for annotation API to complete before proceeding
				await journalService.updateAnnotation(journalId, selectedEmotion);

				setStatus(getConfirmedEmotionStatus(selectedEmotion, 'Selected'));

				// Return the selected emotion to indicate success
				return selectedEmotion;
			} catch (error) {
				// Annotation update failed, ignore silently but don't proceed
				return null;
			}
		}

		// If no journalId, still return success (shouldn't happen in normal flow)
		setStatus(getConfirmedEmotionStatus(selectedEmotion, 'Selected'));
		return selectedEmotion;
	};

	return {
		// Refs
		videoRef,

		// State
		preview,
		status,
		detecting,
		pendingEmotion,
		needsConfirm,
		emotion,
		selectedEmotion,
		journalId,

		// Actions
		startWebcam,
		detectEmotion,
		confirmDetected,
		chooseEmotion,
	};
}
